import type { ReactNode } from 'react';
import { usePermissions } from './permissions-context';

// --- Types ---

export interface RequirePermissionProps {
  /** ALL-of check — user must have every permission in this array */
  allOf?: string[];
  /** ANY-of check — user must have at least one permission in this array */
  anyOf?: string[];
  /** Fallback content to render when permission check fails (defaults to null) */
  fallback?: ReactNode;
  children: ReactNode;
  /** Single permission check — user must have this permission */
  permission?: string;
}

// --- Helpers ---

function warnInvalidPropCount(providedCount: number): void {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
    console.warn(
      '[RequirePermission] Exactly one of `permission`, `allOf`, or `anyOf` must be provided. ' +
        `Received ${providedCount} prop(s). Rendering fallback (fail-closed).`,
    );
  }
}

function warnEmptyArray(): void {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
    console.warn(
      '[RequirePermission] `allOf` and `anyOf` must be non-empty arrays. ' +
        'Received an empty array. Rendering fallback (fail-closed).',
    );
  }
}

function resolveAccess(
  permission: string | undefined,
  allOf: string[] | undefined,
  anyOf: string[] | undefined,
  hasPermission: (p: string) => boolean,
  hasAllPermissions: (perms: string[]) => boolean,
  hasAnyPermission: (perms: string[]) => boolean,
): boolean {
  if (permission !== undefined) {
    return hasPermission(permission);
  }
  if (allOf !== undefined) {
    return hasAllPermissions(allOf);
  }
  return hasAnyPermission(anyOf!);
}

// --- Component ---

export function RequirePermission({
  allOf,
  anyOf,
  children,
  fallback = null,
  permission,
}: RequirePermissionProps): React.JSX.Element {
  const { hasAllPermissions, hasAnyPermission, hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return <>{fallback}</>;
  }

  const providedCount = [permission, allOf, anyOf].filter((p) => p !== undefined).length;

  if (providedCount !== 1) {
    warnInvalidPropCount(providedCount);
    return <>{fallback}</>;
  }

  if ((allOf !== undefined && allOf.length === 0) || (anyOf !== undefined && anyOf.length === 0)) {
    warnEmptyArray();
    return <>{fallback}</>;
  }

  const hasAccess = resolveAccess(permission, allOf, anyOf, hasPermission, hasAllPermissions, hasAnyPermission);

  return <>{hasAccess ? children : fallback}</>;
}
